import { useState, type FormEvent } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { cn } from '@/lib/cn';

const JURISDICTIONS = [
  'CABA',
  'Buenos Aires',
  'Catamarca',
  'Chaco',
  'Chubut',
  'Córdoba',
  'Corrientes',
  'Entre Ríos',
  'Formosa',
  'Jujuy',
  'La Pampa',
  'La Rioja',
  'Mendoza',
  'Misiones',
  'Neuquén',
  'Río Negro',
  'Salta',
  'San Juan',
  'San Luis',
  'Santa Cruz',
  'Santa Fe',
  'Santiago del Estero',
  'Tierra del Fuego',
  'Tucumán',
] as const;

type Audience = 'docentes' | 'directivos' | 'especialistas' | 'desarrolladores';

/**
 * WaitlistForm — segmented by audience.
 *
 * v0: client-only state. No backend wired.
 * TODO: connect to backend signup endpoint when product owner defines the storage.
 */
export function WaitlistForm({
  audience,
  className,
}: {
  audience: Audience;
  className?: string;
}) {
  const [submitted, setSubmitted] = useState(false);

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div
        role="status"
        className={cn(
          'rounded-lg border border-status-covered/40 bg-status-covered/5 p-6',
          className,
        )}
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-status-covered text-paper">
            <Check size={14} />
          </span>
          <div>
            <p className="font-serif text-lg italic text-ink">
              Te anotamos en la lista de espera.
            </p>
            <p className="mt-2 text-sm text-ink-muted">
              Vamos a escribirte cuando tu jurisdicción esté disponible. No mandamos newsletter
              promocional — sólo avisos cuando hay novedad real.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={cn('flex flex-col gap-5', className)} noValidate>
      <Field label="Email" required>
        <input
          type="email"
          name="email"
          required
          autoComplete="email"
          className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink placeholder:text-ink-subtle"
          placeholder="tu@email.com"
        />
      </Field>

      {audience === 'docentes' && (
        <>
          <Field label="Jurisdicción" required>
            <select
              name="jurisdiction"
              required
              className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
              defaultValue=""
            >
              <option value="" disabled>
                Elegí una
              </option>
              {JURISDICTIONS.map((j) => (
                <option key={j} value={j}>
                  {j}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Nivel">
            <div className="flex flex-wrap gap-3 text-sm">
              {(['inicial', 'primaria', 'secundaria'] as const).map((lvl) => (
                <label key={lvl} className="inline-flex items-center gap-2">
                  <input type="checkbox" name="level" value={lvl} />
                  <span className="capitalize text-ink-muted">{lvl}</span>
                </label>
              ))}
            </div>
          </Field>
        </>
      )}

      {audience === 'directivos' && (
        <>
          <Field label="Institución" required>
            <input
              type="text"
              name="institution"
              required
              className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
            />
          </Field>
          <Field label="Jurisdicción" required>
            <select
              name="jurisdiction"
              required
              className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
              defaultValue=""
            >
              <option value="" disabled>
                Elegí una
              </option>
              {JURISDICTIONS.map((j) => (
                <option key={j} value={j}>
                  {j}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Tu rol">
            <input
              type="text"
              name="role"
              className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
              placeholder="Director, supervisor, etc."
            />
          </Field>
        </>
      )}

      {audience === 'especialistas' && (
        <>
          <Field label="Nombre" required>
            <input
              type="text"
              name="name"
              required
              className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
            />
          </Field>
          <Field label="Institución / Equipo">
            <input
              type="text"
              name="institution"
              className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
            />
          </Field>
          <Field label="Modalidad de colaboración">
            <select
              name="mode"
              className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
              defaultValue=""
            >
              <option value="" disabled>Elegí una</option>
              <option value="curaduria">Curaduría</option>
              <option value="analisis">Análisis</option>
              <option value="institucional">Conversación institucional</option>
              <option value="otra">Otra</option>
            </select>
          </Field>
        </>
      )}

      {audience === 'desarrolladores' && (
        <>
          <Field label="Nombre / equipo" required>
            <input
              type="text"
              name="name"
              required
              className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
            />
          </Field>
          <Field label="Producto / agente que querés conectar">
            <input
              type="text"
              name="product"
              className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
              placeholder="Tutor IA para Lengua, asistente docente, etc."
            />
          </Field>
        </>
      )}

      <label className="flex items-start gap-2 text-xs text-ink-muted">
        <input type="checkbox" required className="mt-0.5" />
        <span>
          Acepto que me contacten por correo. No comparten mis datos con terceros.
        </span>
      </label>

      <div>
        <Button type="submit" variant="primary">
          Anotarme en la lista
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-ink">
        {label}
        {required && <span className="ml-1 text-status-progress">*</span>}
      </span>
      {children}
    </label>
  );
}
